import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { toast } from 'sonner';
import OverdueInvoiceCard from './components/OverdueInvoiceCard';
import OverdueStatisticsCards from './components/OverdueStatisticsCards';
import ReminderHistoryModal from './components/ReminderHistoryModal';

/**
 * Page de gestion des rappels de paiement
 * Affiche les factures en retard selon la configuration définie dans payment_notification_settings
 */
const RappelDePaiement = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  
  const [loading, setLoading] = useState(true);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    country: 'all',
    dentist: 'all',
    dateFrom: '',
    dateTo: '',
    severity: 'all', // all, warning, critical
    itemType: 'all', // all, invoice, payment_phase
    search: ''
  });

  // Current currency based on filter
  const [currentCurrency, setCurrentCurrency] = useState('EUR');

  // Reference data
  const [countries, setCountries] = useState([]);
  const [dentists, setDentists] = useState([]);
  
  // Current user
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Modal states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedInvoiceForHistory, setSelectedInvoiceForHistory] = useState(null);

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Facturation', path: '/billing-overview', icon: 'CreditCard' },
    { label: 'Rappels de paiement', path: '/rappel-de-paiement', icon: 'Bell' }
  ];

  // ==========================================
  // INITIALIZATION
  // ==========================================

  useEffect(() => {
    initializePage();
  }, []);

  useEffect(() => {
    if (currentUser && notificationSettings) {
      fetchOverdueInvoices();
    }
  }, [currentUser, notificationSettings]);

  useEffect(() => {
    filterInvoices();
    
    // Update current currency based on country filter
    if (filters.country === 'all') {
      setCurrentCurrency(''); // Empty for mixed currencies
    } else {
      const selectedCountry = countries.find(c => c.id === filters.country);
      if (selectedCountry) {
        setCurrentCurrency(selectedCountry.currency_code);
      }
    }
  }, [filters, overdueInvoices, countries]);

  const initializePage = async () => {
    try {
      setLoading(true);
      await getCurrentUser();
      await fetchNotificationSettings();
      await fetchCountries();
      await fetchDentists();
    } catch (error) {
      console.error('Error initializing page:', error);
      toast.error('Erreur lors de l\'initialisation de la page');
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // DATA FETCHING
  // ==========================================

  const getCurrentUser = async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;

      setCurrentUser(user);

      // Check if admin
      const { data: roleData } = await supabase
        .from('user_role_assignments')
        .select('user_roles(role_type)')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (roleData?.user_roles?.role_type === 'administrator') {
        setIsAdmin(true);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('payment_notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      // Use default settings if none exist
      const settings = data || {
        enable_overdue_alerts: true,
        escalation_days: 3,
        overdue_check_frequency: 'daily',
        reminder_days_before: 7
      };

      setNotificationSettings(settings);
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      // Use default settings
      setNotificationSettings({
        enable_overdue_alerts: true,
        escalation_days: 3,
        overdue_check_frequency: 'daily',
        reminder_days_before: 7
      });
    }
  };

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('id, country_name, country_code, currency_code')
        .eq('is_active', true)
        .order('country_name');

      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchDentists = async () => {
    try {
      const { data: profiles, error } = await supabase
        .from('user_profiles')
        .select(`
          id,
          full_name,
          email,
          country:countries(
            id,
            country_name,
            currency_code
          )
        `)
        .order('full_name');

      if (error) throw error;

      // Filter to only dentist clients
      const dentistsList = [];
      for (const profile of profiles || []) {
        const { data: roleData } = await supabase
          .from('user_role_assignments')
          .select('user_roles(role_type)')
          .eq('user_id', profile.id)
          .eq('is_active', true)
          .single();

        if (roleData?.user_roles?.role_type === 'dentist_client') {
          dentistsList.push(profile);
        }
      }

      setDentists(dentistsList);
    } catch (error) {
      console.error('Error fetching dentists:', error);
    }
  };

  const fetchOverdueInvoices = async () => {
    try {
      setLoading(true);

      const today = new Date().toISOString().split('T')[0];

      // Fetch overdue invoices (full invoices, not phases)
      const { data: invoices, error: invoicesError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          client_name,
          client_email,
          issue_date,
          due_date,
          payment_date,
          subtotal,
          tax_amount,
          total_amount,
          amount_paid,
          balance_due,
          discount_amount,
          status,
          payment_method,
          notes,
          client_id,
          country_id,
          created_at,
          countries (
            id,
            country_name,
            country_code,
            currency_code
          ),
          case_phase_invoices (
            case:cases (
              id,
              case_number,
              patient:patients (
                id,
                name,
                patient_code
              ),
              dentist:user_profiles!cases_user_profile_id_fkey (
                id,
                full_name,
                email
              )
            )
          )
        `)
        .neq('status', 'paid')
        .lt('due_date', today)
        .order('due_date', { ascending: true });

      if (invoicesError) throw invoicesError;

      // Fetch overdue payment phases
      const { data: paymentPhases, error: phasesError } = await supabase
        .from('payment_phases')
        .select(`
          id,
          invoice_id,
          phase_number,
          phase_name,
          phase_description,
          amount,
          due_date,
          payment_date,
          status,
          payment_method,
          notes,
          milestone_status,
          milestone_completion_percentage,
          invoice:invoices (
            id,
            invoice_number,
            client_name,
            client_email,
            client_id,
            country_id,
            countries (
              id,
              country_name,
              country_code,
              currency_code
            ),
            case_phase_invoices (
              case:cases (
                id,
                case_number,
                patient:patients (
                  id,
                  name,
                  patient_code
                ),
                dentist:user_profiles!cases_user_profile_id_fkey (
                  id,
                  full_name,
                  email
                )
              )
            )
          )
        `)
        .neq('status', 'paid')
        .lt('due_date', today)
        .order('due_date', { ascending: true });

      if (phasesError) throw phasesError;

      const escalationDays = notificationSettings?.escalation_days || 3;

      // Process full invoices
      const processedInvoices = (invoices || []).map(invoice => {
        const dueDate = new Date(invoice.due_date);
        const todayDate = new Date(today);
        const daysOverdue = Math.floor((todayDate - dueDate) / (1000 * 60 * 60 * 24));
        
        let severity = 'warning';
        if (daysOverdue >= escalationDays * 3) {
          severity = 'critical';
        } else if (daysOverdue >= escalationDays) {
          severity = 'urgent';
        }

        const casePhaseInvoice = invoice.case_phase_invoices?.[0];
        const caseInfo = casePhaseInvoice?.case;
        const dentistInfo = caseInfo?.dentist;
        const patientInfo = caseInfo?.patient;

        return {
          ...invoice,
          itemType: 'invoice',
          daysOverdue,
          severity,
          currencyCode: invoice.countries?.currency_code || 'EUR',
          countryName: invoice.countries?.country_name || '',
          dentistId: dentistInfo?.id || invoice.client_id,
          dentistName: dentistInfo?.full_name || invoice.client_name,
          dentistEmail: dentistInfo?.email || invoice.client_email,
          caseNumber: caseInfo?.case_number,
          patientName: patientInfo?.name,
          patientCode: patientInfo?.patient_code
        };
      });

      // Process payment phases
      const processedPhases = (paymentPhases || []).map(phase => {
        const dueDate = new Date(phase.due_date);
        const todayDate = new Date(today);
        const daysOverdue = Math.floor((todayDate - dueDate) / (1000 * 60 * 60 * 24));
        
        let severity = 'warning';
        if (daysOverdue >= escalationDays * 3) {
          severity = 'critical';
        } else if (daysOverdue >= escalationDays) {
          severity = 'urgent';
        }

        const invoice = phase.invoice;
        const casePhaseInvoice = invoice?.case_phase_invoices?.[0];
        const caseInfo = casePhaseInvoice?.case;
        const dentistInfo = caseInfo?.dentist;
        const patientInfo = caseInfo?.patient;

        return {
          id: phase.id,
          itemType: 'payment_phase',
          invoice_number: `${invoice?.invoice_number} - Phase ${phase.phase_number}`,
          phase_number: phase.phase_number,
          phase_name: phase.phase_name,
          phase_description: phase.phase_description,
          parentInvoiceNumber: invoice?.invoice_number,
          parentInvoiceId: invoice?.id,
          client_name: invoice?.client_name,
          client_email: invoice?.client_email,
          issue_date: invoice?.issue_date,
          due_date: phase.due_date,
          payment_date: phase.payment_date,
          total_amount: phase.amount,
          amount_paid: 0, // Payment phases don't track partial payments
          balance_due: phase.amount,
          status: phase.status,
          payment_method: phase.payment_method,
          notes: phase.notes,
          milestone_status: phase.milestone_status,
          milestone_completion_percentage: phase.milestone_completion_percentage,
          client_id: invoice?.client_id,
          country_id: invoice?.country_id,
          daysOverdue,
          severity,
          currencyCode: invoice?.countries?.currency_code || 'EUR',
          countryName: invoice?.countries?.country_name || '',
          dentistId: dentistInfo?.id || invoice?.client_id,
          dentistName: dentistInfo?.full_name || invoice?.client_name,
          dentistEmail: dentistInfo?.email || invoice?.client_email,
          caseNumber: caseInfo?.case_number,
          patientName: patientInfo?.name,
          patientCode: patientInfo?.patient_code
        };
      });

      // Combine and sort by days overdue (most overdue first)
      const allOverdueItems = [...processedInvoices, ...processedPhases].sort(
        (a, b) => b.daysOverdue - a.daysOverdue
      );

      setOverdueInvoices(allOverdueItems);
      setFilteredInvoices(allOverdueItems);
    } catch (error) {
      console.error('Error fetching overdue invoices:', error);
      toast.error('Erreur lors du chargement des factures en retard');
      setOverdueInvoices([]);
      setFilteredInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FILTERING
  // ==========================================

  const filterInvoices = () => {
    let filtered = [...overdueInvoices];

    // Country filter
    if (filters.country !== 'all') {
      filtered = filtered.filter(inv => inv.country_id === filters.country);
    }

    // Dentist filter
    if (filters.dentist !== 'all') {
      filtered = filtered.filter(inv => inv.dentistId === filters.dentist);
    }

    // Date range filter
    if (filters.dateFrom) {
      filtered = filtered.filter(inv => 
        new Date(inv.due_date) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(inv => 
        new Date(inv.due_date) <= new Date(filters.dateTo)
      );
    }

    // Severity filter
    if (filters.severity !== 'all') {
      filtered = filtered.filter(inv => inv.severity === filters.severity);
    }

    // Item type filter
    if (filters.itemType !== 'all') {
      filtered = filtered.filter(inv => inv.itemType === filters.itemType);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(inv =>
        inv.invoice_number?.toLowerCase().includes(searchLower) ||
        inv.parentInvoiceNumber?.toLowerCase().includes(searchLower) ||
        inv.phase_name?.toLowerCase().includes(searchLower) ||
        inv.dentistName?.toLowerCase().includes(searchLower) ||
        inv.dentistEmail?.toLowerCase().includes(searchLower) ||
        inv.caseNumber?.toLowerCase().includes(searchLower) ||
        inv.patientName?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredInvoices(filtered);
  };

  const handleResetFilters = () => {
    setFilters({
      country: 'all',
      dentist: 'all',
      dateFrom: '',
      dateTo: '',
      severity: 'all',
      itemType: 'all',
      search: ''
    });
  };

  // ==========================================
  // ACTIONS
  // ==========================================

/**
 * SIMPLIFIED handleSendReminder - Only saves to notification tables
 * NO email sending, NO Edge Functions, NO SMS
 * Just saves to: dashboard_notifications, notifications, and notification_history
 */
/**
 * CORRECTED handleSendReminder - Uses reminder_template (not overdue_template)
 * Replace your current handleSendReminder function with this one
 */
const handleSendReminder = async (invoice) => {
  try {
    toast.info(`Envoi du rappel pour la facture ${invoice.invoice_number}...`);

    // 1. Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('Utilisateur non authentifié');
    }

    // 2. Get notification settings for template
    const { data: settings } = await supabase
      .from('payment_notification_settings')
      .select('*')
      .eq('user_id', user.id)
      .single();

    // 3. Build the reminder message using REMINDER_TEMPLATE (not overdue_template)
    // Default template if none exists
    const template = settings?.reminder_template || 
      'Rappel{reminder_number} : Votre paiement de {amount} pour la facture {invoice_number} est dû le {due_date}.';

    const reminderNumber = invoice.daysOverdue > 1 ? ` ${invoice.daysOverdue}` : '';
    
    const message = template
      .replace(/{reminder_number}/g, reminderNumber)
      .replace(/{invoice_number}/g, invoice.invoice_number)
      .replace(/{amount}/g, formatCurrency(invoice.balance_due, invoice.currencyCode))
      .replace(/{days_overdue}/g, invoice.daysOverdue.toString())
      .replace(/{due_date}/g, formatDate(invoice.due_date))
      .replace(/{client_name}/g, invoice.dentistName)
      .replace(/{balance_due}/g, formatCurrency(invoice.balance_due, invoice.currencyCode));

    const subject = `Rappel de paiement - Facture ${invoice.invoice_number}`;

    // 4. Determine recipient user_id and invoice_id
    const recipientUserId = invoice.client_id || invoice.dentistId;
    const invoiceIdToSave = invoice.itemType === 'payment_phase' ? invoice.parentInvoiceId : invoice.id;

    if (!recipientUserId) {
      throw new Error('Impossible de déterminer le destinataire du rappel');
    }

    console.log('📤 Sending reminder:', {
      invoice_number: invoice.invoice_number,
      recipient: recipientUserId,
      template_used: 'reminder_template', // ← Changed from overdue_template
      message: message.substring(0, 50) + '...'
    });

    // 5. Save to dashboard_notifications table
    const { data: dashboardData, error: dashboardError } = await supabase
      .from('dashboard_notifications')
      .insert({
        user_id: recipientUserId,
        title: subject,
        message: message,
        type: 'payment_reminder',
        action_url: `/billing-overview?invoice=${invoiceIdToSave}`,
        action_label: 'Voir la facture',
        invoice_id: invoiceIdToSave,
        priority: invoice.severity === 'critical' ? 10 : invoice.severity === 'urgent' ? 7 : 5,
        is_read: false,
        is_dismissed: false
      })
      .select();

    if (dashboardError) {
      console.error('❌ Dashboard notification error:', dashboardError);
      throw dashboardError;
    }

    console.log('✅ Dashboard notification saved');

    // 6. Save to notifications table (simple notification system)
    const { data: notifData, error: notifError } = await supabase
      .from('notifications')
      .insert({
        user_id: recipientUserId,
        type: invoice.severity === 'critical' ? 'urgent' : invoice.severity === 'urgent' ? 'warning' : 'info',
        title: subject,
        message: message,
        link: `/billing-overview?invoice=${invoiceIdToSave}`,
        is_read: false,
        metadata: {
          invoice_id: invoiceIdToSave,
          invoice_number: invoice.invoice_number,
          amount: invoice.balance_due,
          currency: invoice.currencyCode,
          days_overdue: invoice.daysOverdue,
          severity: invoice.severity,
          source: 'payment_reminder_page',
          template_used: 'reminder_template' // ← Track which template was used
        }
      })
      .select();

    if (notifError) {
      console.error('❌ Notifications table error:', notifError);
      // Don't throw - this is a secondary table
    } else {
      console.log('✅ Notification (simple table) saved');
    }

    // 7. Log to notification_history for audit trail
    const { data: historyData, error: historyError } = await supabase
      .from('notification_history')
      .insert({
        user_id: recipientUserId,
        invoice_id: invoiceIdToSave,
        notification_type: 'payment_reminder', // ← Changed from 'overdue_alert'
        channel: 'dashboard',
        subject: subject,
        message: message,
        status: 'sent',
        sent_at: new Date().toISOString(),
        metadata: {
          invoice_number: invoice.invoice_number,
          parent_invoice_number: invoice.parentInvoiceNumber || null,
          days_overdue: invoice.daysOverdue,
          amount: invoice.balance_due,
          currency: invoice.currencyCode,
          channels_used: ['dashboard'],
          dashboard_notified: true,
          is_payment_phase: invoice.itemType === 'payment_phase',
          phase_number: invoice.phase_number || null,
          phase_name: invoice.phase_name || null,
          severity: invoice.severity,
          dentist_name: invoice.dentistName,
          dentist_email: invoice.dentistEmail,
          source: 'payment_reminder_page',
          template_used: 'reminder_template' // ← Track which template was used
        }
      })
      .select();

    if (historyError) {
      console.error('❌ Notification history error:', historyError);
      // Don't throw - this is a logging table
    } else {
      console.log('✅ Notification history saved');
    }

    // 8. Optionally log to payment_reminder_logs (if table exists)
    try {
      await supabase.from('payment_reminder_logs').insert({
        invoice_id: invoiceIdToSave,
        payment_phase_id: invoice.itemType === 'payment_phase' ? invoice.id : null,
        sent_at: new Date().toISOString(),
        reminder_type: 'payment_reminder', // ← Changed from 'overdue'
        channel: 'dashboard',
        recipient_email: invoice.dentistEmail,
        status: 'sent',
        template_used: 'reminder_template', // ← Changed from 'overdue_template'
        message_content: message,
        sent_by_user_id: user.id
      });
      console.log('✅ payment_reminder_logs saved (optional)');
    } catch (error) {
      // Table might not exist - that's okay
      console.log('ℹ️ payment_reminder_logs table not found (optional)');
    }

    // 9. Show success message
    toast.success('✅ Rappel envoyé avec succès !', {
      description: `Notification envoyée au tableau de bord de ${invoice.dentistName}`,
      duration: 5000
    });

    console.log('✅ Reminder sent successfully!');

    // 10. Refresh data to show updated counts
    await fetchOverdueInvoices();

  } catch (error) {
    console.error('❌ Error sending reminder:', error);
    toast.error('Erreur lors de l\'envoi du rappel', {
      description: error.message || 'Erreur inconnue',
      duration: 7000
    });
  }
};

  const handleBulkSendReminders = async () => {
    try {
      if (filteredInvoices.length === 0) {
        toast.warning('Aucune facture à traiter');
        return;
      }

      const confirmed = window.confirm(
        `Êtes-vous sûr de vouloir envoyer ${filteredInvoices.length} rappel(s) de paiement ?\n\nCela enverra des notifications par email et/ou tableau de bord selon votre configuration.`
      );

      if (!confirmed) return;

      toast.info(`Envoi de ${filteredInvoices.length} rappel(s) en cours...`, {
        duration: Infinity, // Keep showing until done
        id: 'bulk-send'
      });

      let successCount = 0;
      let failureCount = 0;
      const errors = [];

      // Send reminders sequentially to avoid rate limits
      for (let i = 0; i < filteredInvoices.length; i++) {
        const invoice = filteredInvoices[i];
        
        try {
          // Update progress toast
          toast.loading(`Envoi ${i + 1}/${filteredInvoices.length}: ${invoice.invoice_number}...`, {
            id: 'bulk-send'
          });

          // Get settings
          const { data: settings } = await supabase
            .from('payment_notification_settings')
            .select('*')
            .eq('user_id', currentUser.id)
            .single();

          const template = settings?.overdue_template || 
            'Paiement en retard : La facture {invoice_number} d\'un montant de {amount} est en retard de {days_overdue} jours.';

          const message = template
            .replace(/{invoice_number}/g, invoice.invoice_number)
            .replace(/{amount}/g, formatCurrency(invoice.balance_due, invoice.currencyCode))
            .replace(/{days_overdue}/g, invoice.daysOverdue.toString())
            .replace(/{due_date}/g, formatDate(invoice.due_date))
            .replace(/{client_name}/g, invoice.dentistName);

          const subject = `Rappel de paiement - Facture ${invoice.invoice_number}`;
          const channels = settings?.notification_channels || { email: true, dashboard: true };

          let emailSent = false;
          let dashboardNotified = false;

          // Send dashboard notification
          if (channels.dashboard) {
            const { error: dashboardError } = await supabase
              .from('dashboard_notifications')
              .insert({
                user_id: invoice.client_id || invoice.dentistId,
                title: `Rappel de paiement - ${invoice.invoice_number}`,
                message: message,
                type: 'payment_reminder',
                action_url: `/billing-overview?invoice=${invoice.itemType === 'payment_phase' ? invoice.parentInvoiceId : invoice.id}`,
                action_label: 'Voir la facture',
                invoice_id: invoice.itemType === 'payment_phase' ? invoice.parentInvoiceId : invoice.id,
                priority: invoice.severity === 'critical' ? 10 : invoice.severity === 'urgent' ? 7 : 5
              });

            if (!dashboardError) dashboardNotified = true;
          }

          // Log to notification_history
          await supabase.from('notification_history').insert({
            user_id: invoice.client_id || invoice.dentistId,
            invoice_id: invoice.itemType === 'payment_phase' ? invoice.parentInvoiceId : invoice.id,
            notification_type: 'overdue_alert',
            channel: 'dashboard',
            subject: subject,
            message: message,
            status: dashboardNotified ? 'sent' : 'failed',
            sent_at: new Date().toISOString(),
            metadata: {
              invoice_number: invoice.invoice_number,
              days_overdue: invoice.daysOverdue,
              bulk_send: true,
              bulk_index: i + 1,
              bulk_total: filteredInvoices.length
            }
          });

          if (dashboardNotified || emailSent) {
            successCount++;
          } else {
            failureCount++;
            errors.push({ invoice: invoice.invoice_number, error: 'Aucun canal disponible' });
          }

          // Small delay between sends
          await new Promise(resolve => setTimeout(resolve, 300));

        } catch (error) {
          failureCount++;
          errors.push({
            invoice: invoice.invoice_number,
            error: error.message
          });
        }
      }

      // Dismiss progress toast
      toast.dismiss('bulk-send');

      // Show summary
      if (successCount > 0 && failureCount === 0) {
        toast.success(`✅ ${successCount} rappel(s) envoyé(s) avec succès !`, {
          duration: 5000
        });
      } else if (successCount > 0 && failureCount > 0) {
        toast.warning(`⚠️ ${successCount} rappel(s) envoyé(s), ${failureCount} échec(s)`, {
          description: 'Vérifiez l\'historique pour plus de détails',
          duration: 7000
        });
      } else {
        toast.error(`❌ Échec de l'envoi de tous les rappels (${failureCount})`, {
          description: errors.length > 0 ? errors[0].error : 'Erreur inconnue',
          duration: 7000
        });
      }

      // Log bulk operation summary
      try {
        await supabase.from('notification_history').insert({
          user_id: currentUser.id,
          notification_type: 'manual',
          channel: 'system',
          subject: 'Envoi groupé de rappels',
          message: `Envoi groupé: ${successCount} réussis, ${failureCount} échecs sur ${filteredInvoices.length} factures`,
          status: 'sent',
          sent_at: new Date().toISOString(),
          metadata: {
            total_invoices: filteredInvoices.length,
            success_count: successCount,
            failure_count: failureCount,
            errors: errors
          }
        });
      } catch (error) {
        console.error('Error logging bulk summary:', error);
      }

      // Refresh data
      await fetchOverdueInvoices();

    } catch (error) {
      console.error('Error sending bulk reminders:', error);
      toast.dismiss('bulk-send');
      toast.error('Erreur lors de l\'envoi des rappels', {
        description: error.message
      });
    }
  };

  const handleExport = () => {
    try {
      const exportData = filteredInvoices.map(invoice => ({
        'Type': invoice.itemType === 'payment_phase' ? 'Phase de paiement' : 'Facture complète',
        'N° Facture': invoice.invoice_number,
        'Facture Parente': invoice.parentInvoiceNumber || '',
        'N° Phase': invoice.phase_number || '',
        'Nom de Phase': invoice.phase_name || '',
        'Dentiste': invoice.dentistName,
        'Email': invoice.dentistEmail,
        'Pays': invoice.countryName,
        'N° Cas': invoice.caseNumber || '',
        'Patient': invoice.patientName || '',
        'Date Échéance': formatDate(invoice.due_date),
        'Jours de Retard': invoice.daysOverdue,
        'Gravité': getSeverityLabel(invoice.severity),
        'Montant Total': invoice.total_amount,
        'Solde Dû': invoice.balance_due,
        'Devise': invoice.currencyCode,
        'Statut': invoice.status,
        'Statut Jalon': invoice.milestone_status || '',
        'Progression Jalon': invoice.milestone_completion_percentage ? `${invoice.milestone_completion_percentage}%` : ''
      }));

      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header];
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('href', url);
      link.setAttribute('download', `rappels_paiement_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${exportData.length} ligne(s) exportée(s)`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Erreur lors de l\'export des données');
    }
  };

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  const formatCurrency = (value, currencyCode = 'EUR') => {
    // If no currency code (mixed currencies), just show the number
    if (!currencyCode || currencyCode === '') {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value ?? 0);
    }

    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currencyCode,
      }).format(value ?? 0);
    } catch {
      // Fallback to EUR if currency code is invalid
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(value ?? 0);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      warning: 'Attention',
      urgent: 'Urgent',
      critical: 'Critique'
    };
    return labels[severity] || severity;
  };

  // ==========================================
  // STATISTICS
  // ==========================================

  const calculateStatistics = () => {
    const totalOverdue = filteredInvoices.length;
    const totalInvoices = filteredInvoices.filter(inv => inv.itemType === 'invoice').length;
    const totalPhases = filteredInvoices.filter(inv => inv.itemType === 'payment_phase').length;
    
    const totalAmountOverdue = filteredInvoices.reduce(
      (sum, inv) => sum + parseFloat(inv.balance_due || 0),
      0
    );

    const bySeverity = {
      warning: filteredInvoices.filter(inv => inv.severity === 'warning').length,
      urgent: filteredInvoices.filter(inv => inv.severity === 'urgent').length,
      critical: filteredInvoices.filter(inv => inv.severity === 'critical').length
    };

    const averageDaysOverdue = totalOverdue > 0
      ? Math.round(
          filteredInvoices.reduce((sum, inv) => sum + inv.daysOverdue, 0) / totalOverdue
        )
      : 0;

    return {
      totalOverdue,
      totalInvoices,
      totalPhases,
      totalAmountOverdue,
      bySeverity,
      averageDaysOverdue,
      currency: currentCurrency // Add current currency to stats
    };
  };

  const stats = calculateStatistics();

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />

      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
                <Icon name="Bell" size={32} className="text-destructive" />
                Rappels de paiement
              </h1>
              <p className="text-muted-foreground">
                Gestion des factures en retard selon vos paramètres de notification
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={fetchOverdueInvoices}
                iconName="RefreshCw"
                iconPosition="left"
                disabled={loading}
              >
                Actualiser
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
                disabled={filteredInvoices.length === 0}
              >
                Exporter
              </Button>
              <Button
                variant="default"
                onClick={handleBulkSendReminders}
                iconName="Send"
                iconPosition="left"
                disabled={filteredInvoices.length === 0}
              >
                Envoyer tous les rappels ({filteredInvoices.length})
              </Button>
            </div>
          </div>

          {/* Notification Settings Info */}
          {notificationSettings && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-foreground mb-1">
                    Configuration des rappels actuelle
                  </h3>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>
                      • Escalade après <strong>{notificationSettings.escalation_days} jours</strong> de retard
                    </p>
                    <p>
                      • Fréquence de vérification : <strong>{notificationSettings.overdue_check_frequency === 'daily' ? 'Quotidienne' : notificationSettings.overdue_check_frequency}</strong>
                    </p>
                    <p>
                      • Rappel avant échéance : <strong>{notificationSettings.reminder_days_before} jours</strong>
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('/payment-reminder-system', '_blank')}
                  iconName="Settings"
                  iconPosition="left"
                >
                  Modifier la configuration
                </Button>
              </div>
            </div>
          )}

          {/* Statistics Cards */}
          <OverdueStatisticsCards
            stats={stats}
            formatCurrency={formatCurrency}
            loading={loading}
          />

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Filter" size={16} />
              Filtres
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Rechercher
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    placeholder="N° facture, phase, dentiste, cas, patient..."
                    className="w-full pl-10 pr-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Item Type */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Type
                </label>
                <select
                  value={filters.itemType}
                  onChange={(e) => setFilters({ ...filters, itemType: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Tous les types</option>
                  <option value="invoice">Factures complètes</option>
                  <option value="payment_phase">Phases de paiement</option>
                </select>
              </div>

              {/* Severity */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Gravité
                </label>
                <select
                  value={filters.severity}
                  onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Toutes les gravités</option>
                  <option value="warning">Attention (1-{notificationSettings?.escalation_days || 3} jours)</option>
                  <option value="urgent">Urgent ({notificationSettings?.escalation_days || 3}+ jours)</option>
                  <option value="critical">Critique ({(notificationSettings?.escalation_days || 3) * 3}+ jours)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Pays
                </label>
                <select
                  value={filters.country}
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Tous les pays</option>
                  {countries.map(country => (
                    <option key={country.id} value={country.id}>
                      {country.country_name} ({country.currency_code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Dentist */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Dentiste
                </label>
                <select
                  value={filters.dentist}
                  onChange={(e) => setFilters({ ...filters, dentist: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Tous les dentistes</option>
                  {dentists.map(dentist => (
                    <option key={dentist.id} value={dentist.id}>
                      {dentist.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Échéance du
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Échéance au
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Severity */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Gravité
                </label>
                <select
                  value={filters.severity}
                  onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Toutes les gravités</option>
                  <option value="warning">Attention (1-{notificationSettings?.escalation_days || 3} jours)</option>
                  <option value="urgent">Urgent ({notificationSettings?.escalation_days || 3}+ jours)</option>
                  <option value="critical">Critique ({(notificationSettings?.escalation_days || 3) * 3}+ jours)</option>
                </select>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetFilters}
                iconName="X"
                iconPosition="left"
              >
                Réinitialiser
              </Button>
              <p className="text-sm text-muted-foreground ml-auto">
                {filteredInvoices.length} facture(s) en retard affichée(s)
              </p>
            </div>
          </div>

          {/* Overdue Invoices List */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader" size={32} className="animate-spin text-primary mr-3" />
              <span className="text-muted-foreground">Chargement des factures en retard...</span>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-16 bg-card border border-border rounded-lg">
              <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Aucune facture en retard
              </h3>
              <p className="text-muted-foreground">
                {overdueInvoices.length === 0
                  ? 'Tous les paiements sont à jour !'
                  : 'Aucune facture ne correspond à vos critères de recherche'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <Icon name="AlertTriangle" size={20} className="text-destructive" />
                Factures en retard ({filteredInvoices.length})
              </h2>

              <div className="grid grid-cols-1 gap-4">
                {filteredInvoices.map(invoice => (
                  <OverdueInvoiceCard
                    key={invoice.id}
                    invoice={invoice}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    onSendReminder={handleSendReminder}
                    onViewDetails={() => {
                      const targetInvoiceId = invoice.itemType === 'payment_phase' 
                        ? invoice.parentInvoiceId 
                        : invoice.id;
                      window.open(`/billing-overview?invoice=${targetInvoiceId}`, '_blank');
                    }}
                    onViewHistory={() => {
                      setSelectedInvoiceForHistory(invoice);
                      setShowHistoryModal(true);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Reminder History Modal */}
      {showHistoryModal && selectedInvoiceForHistory && (
        <ReminderHistoryModal
          invoice={selectedInvoiceForHistory}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedInvoiceForHistory(null);
          }}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

export default RappelDePaiement;